import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ExternalLink, Info, Loader2 } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

const STATUS = {
  submitted: "Trimisă", confirmed: "Confirmată", pending_payment: "Așteaptă plată", paid: "Plătită",
  cancelled: "Anulată", failed: "Eșuată", abandoned: "Abandonată",
};
const EMPTY = { prenume: "", nume: "", email: "", telefon: "", unitate: "" };
const input = "w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm text-[#373A45] focus:border-[#965EA7] focus:outline-none";

/**
 * „Adaugă cursant” (03.11.2026): somebody put straight into the group by the
 * team, at their request, whatever the site's window or the seats. The
 * payment link then goes through the enrolment's own action, the same email.
 */
export default function AddParticipantDialog({ groupId, open, onClose, onAdded }) {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [sendLink, setSendLink] = useState(true);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY); setConflict(null); setSendLink(true); setReason("");
    api.get(`/admin/groups/${groupId}/add-participant`).then((r) => {
      setInfo(r.data);
      setAmount(r.data.price != null ? String(r.data.price) : "");
    }).catch(() => setInfo(null));
  }, [open, groupId]);

  const custom = info && amount !== "" && Number(String(amount).replace(",", ".")) !== Number(info.price);
  const linkGoes = sendLink && info && !info.institution_pays;

  const submit = async (moveExisting) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/groups/${groupId}/participants`, { ...form, move_existing: moveExisting || null });
      // A moved enrolment keeps its own name: the form's is not written over it.
      const who = (r.data.moved && conflict?.name) || `${form.prenume} ${form.nume}`.trim();
      if (linkGoes) {
        try {
          await api.post(`/admin/enrollments/${r.data.enrollment_id}/send-payment-link`, {
            amount: Number(String(amount).replace(",", ".")), internal_note: custom ? reason : undefined });
          toast.success(`${who} e în grupă și a primit linkul de plată pe email.`);
        } catch (err) {
          toast.error(`${who} e în grupă, dar linkul de plată nu a plecat: ${apiErrorMessage(err, "eroare")} `
            + "Îl trimiți din fișa înscrierii.", { duration: 10000 });
        }
      } else {
        toast.success(r.data.moved ? `Am mutat înscrierea lui ${who} în grupă.` : `${who} e în grupă.`);
      }
      onAdded?.();
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.code) setConflict(d);
      else toast.error(apiErrorMessage(err, "Nu am putut adăuga cursantul."));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, extra = {}) => (
    <label className="grid gap-1 text-xs font-semibold text-[#6B7280]">
      {label}
      <input value={form[key]} onChange={(e) => { setForm({ ...form, [key]: e.target.value }); setConflict(null); }}
        className={input} data-testid={`add-participant-${key}`} {...extra} />
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Adaugă cursant în {info?.group_name || "grupă"}</DialogTitle>
          <DialogDescription>
            Îl adaugi tu, la cererea lui, chiar dacă înscrierile pe site s-au închis. După plată, primește ca oricine
            informațiile suplimentare și contractul.
          </DialogDescription>
        </DialogHeader>

        {info && (
          <div className="grid gap-2 text-sm" data-testid="add-participant-notes">
            {!info.takes && (
              <Note tone="warn">Grupa nu e activă: activeaz-o întâi din fișa ei.</Note>
            )}
            {info.full && (
              <Note tone="warn">Grupa e plină: {info.enrolled} din {info.max_seats} locuri. Îl poți adăuga oricum.</Note>
            )}
            {info.held_sessions > 0 && !info.fped && (
              <Note tone="info">
                S-au ținut deja {info.held_sessions} {info.held_sessions === 1 ? "sesiune" : "sesiuni"}, {info.held_hours} ore
                din {info.total_hours} de live: îi intră la absențe. Pragul e de 20% pe componentă; peste el e nevoie
                de recuperare.
              </Note>
            )}
            {info.institution_pays && (
              <Note tone="info">Locul îl plătește instituția: nu pleacă link de plată. Îl confirmi la „Plătește instituția”.</Note>
            )}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {field("prenume", "Prenume")}
          {field("nume", "Nume")}
          {field("email", "Email", { type: "email", autoComplete: "off" })}
          {field("telefon", "Telefon", { inputMode: "tel" })}
          <div className="sm:col-span-2">{field("unitate", "Unitatea de învățământ (opțional)")}</div>
        </div>

        {info && !info.institution_pays && (
          <div className="grid gap-2 rounded-xl bg-[#F5F6F6] p-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-[#373A45]">
              <input type="checkbox" checked={sendLink} onChange={(e) => setSendLink(e.target.checked)}
                className="accent-[#965EA7]" data-testid="add-participant-send-link" />
              Trimite acum linkul de plată pe email
            </label>
            {sendLink && (
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="grid gap-1 text-xs font-semibold text-[#6B7280]">
                  Suma de plată (RON)
                  <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className={input} />
                </label>
                {custom && (
                  <label className="grid gap-1 text-xs font-semibold text-[#6B7280]">
                    Motivul sumei (intern)
                    <input value={reason} onChange={(e) => setReason(e.target.value)} className={input} />
                  </label>
                )}
              </div>
            )}
          </div>
        )}

        {conflict && (
          <div className="rounded-xl border border-[#d99a2b]/40 bg-[#d99a2b]/10 p-3 text-sm text-[#5c3d0a]" data-testid="add-participant-conflict">
            <p className="font-semibold">{conflict.message}</p>
            {conflict.enrollment_id && (
              <p className="mt-1 text-xs">
                {conflict.name || form.email} · {STATUS[conflict.status] || conflict.status}
                {conflict.expired ? " (expirată)" : ""}{conflict.group_name ? ` · ${conflict.group_name}` : " · fără grupă"}
                {" · "}
                <a href={`/admin/enrollments?e=${conflict.enrollment_id}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 font-semibold text-[#7A4986] underline">
                  Fișa înscrierii <ExternalLink size={11} />
                </a>
              </p>
            )}
            {conflict.code === "existing" && (
              <p className="mt-1 text-xs">Datele din înscrierea existentă rămân cum sunt; se schimbă doar grupa.</p>
            )}
            {conflict.code === "existing" && (
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" disabled={busy} onClick={() => submit(conflict.enrollment_id)}
                  className="rounded-full bg-[#965EA7] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#7A4986]"
                  data-testid="add-participant-move">
                  Mută înscrierea în această grupă
                </button>
                <button type="button" disabled={busy} onClick={() => setConflict(null)}
                  className="rounded-full border border-[#E5E7EB] bg-white px-4 py-1.5 text-xs font-semibold text-[#373A45]">
                  Renunță
                </button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <button type="button" onClick={onClose} disabled={busy}
            className="rounded-full border border-[#E5E7EB] px-4 py-2 text-sm font-semibold text-[#373A45]">Anulează</button>
          <button type="button" onClick={() => submit(null)} disabled={busy || !info?.takes || conflict?.code === "existing"}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-5 py-2 text-sm font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50"
            data-testid="add-participant-submit">
            {busy && <Loader2 size={14} className="animate-spin" />} Adaugă în grupă
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Note({ tone, children }) {
  const warn = tone === "warn";
  return (
    <p className={`flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${warn ? "bg-[#d99a2b]/15 text-[#8a5d12]" : "bg-[#3F8EBA]/10 text-[#2f6d90]"}`}>
      {warn ? <AlertTriangle size={14} className="mt-0.5 shrink-0" /> : <Info size={14} className="mt-0.5 shrink-0" />}
      <span>{children}</span>
    </p>
  );
}
